// Phone sticky Add to Cart. Mirrors the main product form button and presses it, so there is one cart path.
class ProductStickyCart extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.formId);
    this.mainButton = this.form && this.form.querySelector('[data-add-to-cart]');
    if (!this.mainButton) { return; }
    this.addButton = this.querySelector('[data-sticky-cart-add]');
    this.label = this.querySelector('[data-sticky-cart-label]');
    this.price = this.querySelector('[data-sticky-cart-price]');
    this.option = this.querySelector('[data-sticky-cart-option]');
    this.extras = this.querySelector('[data-sticky-cart-extras]');
    // Lives outside the bar, which is aria-hidden whenever it slides away.
    this.status = document.getElementById(this.dataset.statusId);
    try { this.product = JSON.parse(this.form.querySelector('[data-product-json]').textContent); } catch (error) { this.product = null; }

    this.addButton.addEventListener('click', ()=>this.add());
    this.querySelector('[data-sticky-cart-info]').addEventListener('click', ()=>this.scrollToOptions());
    document.addEventListener('theme:variant:change', (event)=>{
      if (event.target.contains(this.form)) { this.variant = event.detail.variant; this.variantChanged = true; this.sync(); }
    });
    document.addEventListener('upsell:added', (event)=>{ this.announce(event.detail.count); this.setPending(false); });
    document.addEventListener('upsell:add-failed', ()=>this.setPending(false));
    // Only writes to the sticky bar, never to the observed elements, so this cannot loop.
    new MutationObserver(()=>this.sync()).observe(this.mainButton, { attributes: true, attributeFilter: ['disabled'], childList: true, characterData: true, subtree: true });
    this.upsellObserver = new MutationObserver(()=>this.refresh());

    this.mainPassed = false;
    this.footerVisible = false;
    this.bundleInView = false;
    // The sign-up popup sits at the bottom of the screen too, so step aside while it is open.
    this.popups = [...document.querySelectorAll('[data-popup-delay] [data-popup-inner]')];
    const popupObserver = new MutationObserver(()=>this.toggle());
    this.popups.forEach((popup)=>popupObserver.observe(popup, { attributes: true, attributeFilter: ['style'] }));

    let ticking = false;
    this.schedule = ()=>{
      if (ticking) { return; }
      ticking = true;
      requestAnimationFrame(()=>{ ticking = false; this.measure(); });
    };
    window.addEventListener('scroll', this.schedule, { passive: true });
    window.addEventListener('resize', this.schedule);

    const footer = document.querySelector('footer.footer');
    if (footer) {
      new IntersectionObserver(([entry])=>{ this.footerVisible = entry.isIntersecting; this.toggle(); }).observe(footer);
    }
    this.watchUpsell();

    this.sync();
    this.schedule();
  }

  // Positions are read live because the theme sets the sticky header height after load.
  measure() {
    const header = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--menu-height-sticky'), 10) || 0;
    this.mainPassed = this.mainButton.getBoundingClientRect().bottom <= header;
    // With extras ticked, the box's own button is the one to press, but only while it is actually in view.
    const bundle = this.bundleButton();
    const rect = bundle && bundle.getBoundingClientRect();
    this.bundleInView = Boolean(rect) && rect.bottom > header && rect.top < window.innerHeight - this.offsetHeight;
    this.toggle();
  }

  // The "Frequently bought together" app renders its box into the form after load and can re-render it.
  watchUpsell() {
    const attach = ()=>{
      const box = this.form.querySelector(':scope > [data-essential-upsell-element="container"]');
      if (!box || box === this.upsell) { return; }
      this.upsell = box;
      this.upsellObserver.disconnect();
      this.upsellObserver.observe(box, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'checked', 'class'] });
      this.refresh();
    };
    attach();
    new MutationObserver(attach).observe(this.form, { childList: true });
    // The box swallows its checkbox events, so listen on the way down and read the result once it has run.
    this.form.addEventListener('click', (event)=>{
      if (this.upsell && this.upsell.contains(event.target)) { setTimeout(()=>this.refresh()); }
    }, true);
  }

  refresh() {
    if (this.refreshing) { return; }
    this.refreshing = true;
    requestAnimationFrame(()=>{ this.refreshing = false; this.sync(); this.measure(); });
  }

  extraCount() {
    return this.upsell ? this.upsell.querySelectorAll('input[type="checkbox"]:checked:not(:disabled)').length : 0;
  }

  bundleButton() {
    const button = this.upsell && this.upsell.querySelector('[data-essential-upsell-element="add-to-cart-button"]');
    return button && !button.disabled && this.extraCount() ? button : null;
  }

  add() {
    if (this.pending) { return; }
    if (this.needsChoice) { this.scrollToOptions(); return; }
    const bundle = this.bundleButton();
    if (bundle) {
      // The box confirms the add itself (upsell-box.js); hold the button until it reports back.
      this.setPending(true);
      this.pendingTimer = setTimeout(()=>this.setPending(false), 8000);
      bundle.click();
      return;
    }
    this.mainButton.click();
  }

  setPending(pending) {
    this.pending = pending;
    clearTimeout(this.pendingTimer);
    this.addButton.toggleAttribute('aria-busy', pending);
    this.addButton.disabled = pending || (this.mainButton.disabled && !this.needsChoice);
  }

  announce(count) {
    if (!this.status) { return; }
    // Cleared first so a repeat of the same message is still read out.
    this.status.textContent = '';
    setTimeout(()=>{ this.status.textContent = this.dataset.addedLabel.replace('{count}', count); }, 100);
    setTimeout(()=>{ this.status.textContent = ''; }, 5000);
  }

  currentVariant() {
    // After a change, a missing variant means that option combination does not exist.
    if (this.variantChanged) { return this.variant || null; }
    const input = this.form.querySelector('[name="id"]');
    if (!this.product || !input) { return null; }
    return this.product.variants.find((variant)=>String(variant.id) === input.value) || null;
  }

  chosenOptions() {
    return [...this.form.querySelectorAll('input[name^="options["]:checked, select[name^="options["]')].map((field)=>field.value).join(' / ');
  }

  unpicked() {
    const names = new Set([...this.form.elements].filter((field)=>field.type === 'radio' && field.name.startsWith('options[')).map((field)=>field.name));
    return [...names].some((name)=>!this.form.elements[name].value);
  }

  sync() {
    const text = this.mainButton.querySelector('[data-add-to-cart-text]');
    const price = this.mainButton.querySelector('[data-button-price]');
    const variant = this.currentVariant();
    // A sold out or missing size still has other sizes to pick, so the button leads there instead of going dead.
    this.needsChoice = this.mainButton.disabled && Boolean(this.product && this.product.available);
    this.addButton.disabled = this.pending || (this.mainButton.disabled && !this.needsChoice);
    const bundle = !this.needsChoice && this.bundleButton();
    const extras = bundle ? this.extraCount() : 0;

    if (this.needsChoice) {
      this.label.textContent = this.dataset.chooseLabel;
    } else if (bundle) {
      this.label.textContent = this.dataset.addManyLabel.replace('{count}', extras + 1);
    } else if (text) {
      this.label.textContent = text.textContent.trim();
    }

    // No size picked yet is not the same as a size that does not exist, so keep the price showing.
    const unpicked = this.needsChoice && !variant && this.unpicked();
    const bundlePrice = bundle && bundle.querySelector('[data-essential-upsell-element="discounted-price"]');
    const mainPrice = unpicked && this.form.closest('.product-single')?.querySelector('[data-product-price]');
    this.price.hidden = (!variant || this.needsChoice) && !unpicked;
    if (bundlePrice) {
      this.price.textContent = bundlePrice.textContent.trim();
    } else if (price && variant) {
      this.price.textContent = price.textContent.trim();
    } else if (mainPrice) {
      this.price.textContent = mainPrice.textContent.trim();
    }

    if (this.needsChoice) {
      const chosen = variant ? variant.title : this.chosenOptions();
      this.option.textContent = chosen !== 'Default Title' ? chosen : '';
      this.extras.textContent = unpicked ? '' : (variant ? this.dataset.soldOutLabel : this.dataset.unavailableLabel);
    } else {
      this.option.textContent = !bundle && variant && variant.title !== 'Default Title' ? variant.title : '';
      this.extras.textContent = extras ? (extras === 1 ? this.dataset.extraOne : this.dataset.extraMany.replace('{count}', extras)) : '';
    }
  }

  toggle() {
    const popupOpen = this.popups.some((popup)=>popup.style.display === 'block');
    const show = this.mainPassed && !this.footerVisible && !popupOpen && !this.bundleInView;
    if (!show && this.contains(document.activeElement)) { this.mainButton.focus({ preventScroll: true }); }
    this.classList.toggle('is-visible', show);
    this.toggleAttribute('inert', !show);
    this.setAttribute('aria-hidden', String(!show));
  }

  scrollToOptions() {
    const wrapper = this.form.closest('[data-product-form-wrapper]');
    const target = (wrapper && wrapper.querySelector('.product__block__variants')) || this.mainButton;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });

    const name = this.dataset.choiceOption;
    const field = (name && (target.querySelector(`input[name="options[${name}]"]:checked`) || target.querySelector(`[name="options[${name}]"]`))) || target.querySelector('input:checked, select');
    const group = (field && (field.closest('fieldset') || field.parentElement)) || target;
    if (field) {
      field.focus({ preventScroll: true });
    } else {
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }

    // Mark the group the shopper was sent to, and keep the mark until the scroll has landed.
    group.classList.add('sticky-cart-target');
    clearTimeout(this.highlightTimer);
    let started = false;
    const start = ()=>{
      if (started) { return; }
      started = true;
      this.highlightTimer = setTimeout(()=>group.classList.remove('sticky-cart-target'), 2000);
    };
    window.addEventListener('scrollend', start, { once: true });
    setTimeout(start, 900);
    group.addEventListener('change', ()=>group.classList.remove('sticky-cart-target'), { once: true });
  }
}

if (!customElements.get('product-sticky-cart')) {
  customElements.define('product-sticky-cart', ProductStickyCart);
}
